import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const CHANGED = 'push-state-changed';

const deviceId = () => {
  let id = localStorage.getItem('push_device_id');
  if (!id) {
    id = `${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem('push_device_id', id);
  }
  return id;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

/** Istruzioni concrete per riaprire il permesso: cambiano da dispositivo a dispositivo */
const blockedHint = () => {
  const ua = navigator.userAgent;
  const installed = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'Notifiche bloccate. Apri Impostazioni iPhone → Notifiche, cerca questa app e attiva "Consenti notifiche".';
  }
  if (/android/i.test(ua)) {
    return installed
      ? 'Notifiche bloccate. Tieni premuta l\'icona dell\'app → Informazioni app → Notifiche, e attivale.'
      : 'Notifiche bloccate. Tocca il lucchetto accanto all\'indirizzo → Autorizzazioni → Notifiche → Consenti, poi ricarica la pagina.';
  }
  return 'Notifiche bloccate. Clicca sul lucchetto accanto all\'indirizzo → Notifiche → Consenti, poi ricarica la pagina.';
};

/** Stato unico delle notifiche push: pannello campanella e Impostazioni → Preferenze
 *  leggono e scrivono la stessa cosa. */
export const usePushNotifications = () => {
  const { API, getAuthHeaders } = useAuth();
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [status, setStatus] = useState(null);     // { configured, devices, push_enabled }
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const getBrowserSub = useCallback(async () => {
    if (!supported) return null;
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    return reg ? reg.pushManager.getSubscription() : null;
  }, [supported]);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/push/status`, getAuthHeaders());
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
    try {
      const sub = await getBrowserSub();
      if (!sub) { setSubscribed(false); return; }
      // se il server usa un'altra coppia di chiavi l'iscrizione non vale più
      const key = (await axios.get(`${API}/push/vapid-key`)).data?.public_key;
      const subKey = sub.options?.applicationServerKey;
      if (key && subKey) {
        const current = btoa(String.fromCharCode(...new Uint8Array(subKey)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        if (current !== key) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          try {
            await axios.delete(`${API}/push/unsubscribe`, { ...getAuthHeaders(), data: { endpoint } });
          } catch { /* verrà rimossa al primo invio fallito */ }
          setSubscribed(false);
          return;
        }
      }
      setSubscribed(true);
    } catch {
      setSubscribed(false);
    }
  }, [API, getAuthHeaders, getBrowserSub]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(CHANGED, onChange);
    return () => window.removeEventListener(CHANGED, onChange);
  }, [refresh]);

  const announce = async () => {
    await refresh();
    window.dispatchEvent(new Event(CHANGED));
  };

  const enable = async () => {
    if (!supported) {
      toast.error('Il tuo browser non supporta le notifiche');
      return;
    }
    const iosNotInstalled = /iphone|ipad|ipod/i.test(navigator.userAgent)
      && !window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone;
    if (iosNotInstalled) {
      toast.error('Su iPhone installa prima l\'app: Impostazioni → Preferenze → Installa l\'app', { duration: 9000 });
      return;
    }
    // Il permesso va chiesto SUBITO, prima di qualsiasi await: Chrome lo nega
    // in automatico se il gesto dell'utente è già scaduto (bug visibile solo
    // su un dominio nuovo, dove il permesso non è ancora stato concesso).
    if (Notification.permission === 'denied') {
      toast.error(blockedHint(), { duration: 14000 });
      return;
    }
    setBusy(true);
    try {
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error(permission === 'denied' ? blockedHint() : 'Permesso non concesso',
          { duration: permission === 'denied' ? 14000 : 6000 });
        return;
      }
      const key = (await axios.get(`${API}/push/vapid-key`)).data?.public_key;
      if (!key) {
        toast.error('Notifiche push non configurate sul server');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const old = await reg.pushManager.getSubscription();
      if (old) await old.unsubscribe();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key)
      });
      await axios.post(`${API}/push/subscribe`,
        { subscription: sub.toJSON(), device: deviceId() }, getAuthHeaders());
      await axios.put(`${API}/notifications/preferences`, { push_enabled: true }, getAuthHeaders());
      toast.success('Notifiche attivate su questo dispositivo');
    } catch (err) {
      toast.error(err.response?.data?.detail || `Attivazione non riuscita: ${err.message}`, { duration: 9000 });
    } finally {
      setBusy(false);
      announce();
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const sub = await getBrowserSub();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe`, { ...getAuthHeaders(), data: { endpoint } });
      }
      await axios.put(`${API}/notifications/preferences`, { push_enabled: false }, getAuthHeaders());
      toast('Notifiche disattivate');
    } catch {
      toast.error('Disattivazione non riuscita');
    } finally {
      setBusy(false);
      announce();
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/push/test`, {}, getAuthHeaders());
      if (res.data?.ok) toast.success(res.data.message || 'Notifica di test inviata');
      else toast.error(res.data?.message || 'Invio non riuscito', { duration: 9000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invio non riuscito: riprova tra poco', { duration: 9000 });
    } finally {
      setBusy(false);
      announce();
    }
  };

  const removeOtherDevices = async () => {
    setBusy(true);
    try {
      const sub = await getBrowserSub();
      const res = await axios.delete(`${API}/push/other-devices`,
        { ...getAuthHeaders(), data: { endpoint: sub?.endpoint || '' } });
      toast.success(res.data.removed > 0
        ? `Scollegati ${res.data.removed} dispositivi`
        : 'Nessun altro dispositivo da scollegare');
    } catch {
      toast.error('Operazione non riuscita');
    } finally {
      setBusy(false);
      announce();
    }
  };

  // acceso solo se il server le consente E questo dispositivo è iscritto
  const enabled = subscribed && status?.push_enabled !== false;

  return {
    supported, enabled, subscribed, busy,
    devices: status?.devices ?? 0,
    configured: status?.configured !== false,
    toggle: (value) => (value ? enable() : disable()),
    sendTest, removeOtherDevices, refresh,
  };
};
